import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  addShipmentEvent,
  getShipmentTracking,
  sendShipmentMessage,
  updateShipmentEta,
  updateShipmentMilestone,
  updateShipmentStatus,
  updateShipmentTracking,
} from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

const STATUSES = [
  "Factory Pickup Pending",
  "Factory Picked Up",
  "Gate In",
  "Loaded on Vessel",
  "Departed",
  "In Transit",
  "Transshipment",
  "Arrived",
  "Customs Clearance",
  "Out for Delivery",
  "Delivered",
  "Delayed",
  "Cancelled",
];

export default function ShipmentDetail() {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const [trk, setTrk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const isAdmin = !!localStorage.getItem("demaxtore_admin_token");

  const [vessel, setVessel] = useState("");
  const [voyage, setVoyage] = useState("");
  const [eta, setEta] = useState("");
  const [etd, setEtd] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [containerType, setContainerType] = useState("");
  const [grossWeight, setGrossWeight] = useState("");
  const [netWeight, setNetWeight] = useState("");
  const [packages, setPackages] = useState("");
  const [opsNotes, setOpsNotes] = useState("");
  const [eventText, setEventText] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventRemarks, setEventRemarks] = useState("");
  const [statusLocation, setStatusLocation] = useState("");
  const [statusRemarks, setStatusRemarks] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getShipmentTracking(shipmentId);
      setTrk(data);
      setVessel(data.vessel || "");
      setVoyage(data.voyage || "");
      setEta(data.eta || "");
      setEtd(data.etd || "");
      setContainerNumber(data.container_number || "");
      setSealNumber(data.seal_number || "");
      setContainerType(data.container_type || "");
      setGrossWeight(data.gross_weight != null ? String(data.gross_weight) : "");
      setNetWeight(data.net_weight != null ? String(data.net_weight) : "");
      setPackages(data.number_of_packages != null ? String(data.number_of_packages) : "");
      setOpsNotes(data.ops_notes || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load shipment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await sendShipmentMessage(shipmentId, msg.trim());
      toast.success("Message sent");
      setMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }
  if (!trk) {
    return (
      <div className="min-h-screen grid place-items-center text-neutral-500">
        Shipment not found.
      </div>
    );
  }

  const events = [...(trk.events || [])].sort((a, b) =>
    String(b.at || "").localeCompare(String(a.at || ""))
  );

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.shipmentDetail}>
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/workspace/shipments" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Shipment Tracking
            </span>
          </Link>
          <Button variant="outline" className="rounded-sm h-10" onClick={() => window.print()}>
            Download Shipment Summary
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <button
          type="button"
          onClick={() => navigate("/workspace/shipments")}
          className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> All shipments
        </button>

        {/* Section 1 — Summary */}
        <section className="border border-neutral-200 bg-white rounded-sm p-6 lg:p-8">
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
            Shipment Summary
          </div>
          <h1 className="mt-3 text-3xl font-light tracking-tight font-mono text-[hsl(217,90%,25%)]">
            {trk.number}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
              {trk.status}
            </Badge>
            <div className="flex items-center gap-2 min-w-[140px]">
              <div className="flex-1 h-1.5 bg-neutral-100 rounded-sm overflow-hidden w-24">
                <div
                  className="h-full bg-emerald-600"
                  style={{ width: `${trk.progress || 0}%` }}
                />
              </div>
              <span className="text-xs text-neutral-500">{trk.progress || 0}%</span>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Meta
              label="Purchase Order"
              value={
                trk.po_id ? (
                  <Link
                    to={`/workspace/purchase-orders/${trk.po_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {trk.po_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta label="Container Number" value={trk.container_number || "—"} />
            <Meta label="Booking Reference" value={trk.booking_reference || "—"} />
            <Meta label="Carrier" value={trk.carrier || "—"} />
            <Meta label="Vessel" value={trk.vessel || "—"} />
            <Meta label="Voyage" value={trk.voyage || "—"} />
            <Meta label="Origin Port" value={trk.origin_port || trk.origin || "—"} />
            <Meta
              label="Destination Port"
              value={trk.destination_port || trk.destination || "—"}
            />
            <Meta label="ETD" value={trk.etd || "—"} />
            <Meta label="ETA" value={trk.eta || "—"} />
            <Meta
              label="FreightIQ"
              value={
                trk.freightiq_id ? (
                  <Link
                    to={`/workspace/freightiq/${trk.freightiq_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {trk.freightiq_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Inspection"
              value={
                trk.inspection_id ? (
                  <Link
                    to={`/workspace/inspections/${trk.inspection_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {trk.inspection_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
          </div>
        </section>

        {/* Section 2 — Progress */}
        <section data-testid={WORKSPACE.shipmentProgress}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Shipment Progress
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(60,5%,96%)] text-left">
                <tr>
                  {["Milestone", "Completed", "Date", "Time", "Location", "Remarks"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(trk.milestones || []).map((m) => (
                  <tr key={m.key} className="border-t border-neutral-100">
                    <td className="px-4 py-3 font-medium">{m.label}</td>
                    <td className="px-4 py-3">
                      {m.completed || m.status === "done" ? (
                        <span className="text-emerald-700 text-xs uppercase tracking-wider font-semibold">
                          Yes
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-xs uppercase tracking-wider">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{m.date || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">{m.time || "—"}</td>
                    <td className="px-4 py-3">{m.location || "—"}</td>
                    <td className="px-4 py-3 text-neutral-500">{m.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 3 — Container */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Container Information
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta label="Container Number" value={trk.container_number || "—"} />
            <Meta label="Seal Number" value={trk.seal_number || "—"} />
            <Meta label="Container Type" value={trk.container_type || "—"} />
            <Meta label="Gross Weight" value={trk.gross_weight ?? "—"} />
            <Meta label="Net Weight" value={trk.net_weight ?? "—"} />
            <Meta label="Number of Packages" value={trk.number_of_packages ?? "—"} />
          </div>
        </section>

        {/* Section 4 — Events */}
        <section data-testid={WORKSPACE.shipmentEvents}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Shipment Events
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
            {events.length === 0 ? (
              <div className="p-8 text-sm text-neutral-400 text-center">No events yet</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(60,5%,96%)] text-left">
                  <tr>
                    {["Event", "Location", "Date", "Time", "Remarks"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-t border-neutral-100">
                      <td className="px-4 py-3 font-medium">{e.event}</td>
                      <td className="px-4 py-3">{e.location || "—"}</td>
                      <td className="px-4 py-3 text-neutral-600">{e.date || "—"}</td>
                      <td className="px-4 py-3 text-neutral-600">{e.time || "—"}</td>
                      <td className="px-4 py-3 text-neutral-500">{e.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Section 5 — Timeline */}
        <section data-testid={WORKSPACE.shipmentTimeline}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Timeline
          </h2>
          <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
            {(trk.timeline || []).map((t) => (
              <li key={t.key} className="p-4 flex items-center gap-3 text-sm">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    t.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                  }`}
                />
                <span className="font-medium flex-1">{t.label}</span>
                <span className="text-neutral-400 text-xs">
                  {t.at ? new Date(t.at).toLocaleString() : "Pending"}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Message */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Send Message
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Message operations about this shipment…"
              className="h-11 rounded-sm flex-1"
            />
            <Button variant="outline" className="rounded-sm h-11" onClick={sendMsg}>
              <MessageSquare className="w-4 h-4 mr-2" /> Send
            </Button>
          </div>
        </section>

        {/* Ops */}
        {isAdmin && (
          <section
            data-testid={WORKSPACE.shipmentOpsPanel}
            className="border border-[hsl(217,90%,25%)]/20 bg-white rounded-sm p-6 space-y-6"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(217,90%,25%)]">
              Operations
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Vessel
                </span>
                <Input
                  value={vessel}
                  onChange={(e) => setVessel(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Voyage
                </span>
                <Input
                  value={voyage}
                  onChange={(e) => setVoyage(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Container number
                </span>
                <Input
                  value={containerNumber}
                  onChange={(e) => setContainerNumber(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Seal number
                </span>
                <Input
                  value={sealNumber}
                  onChange={(e) => setSealNumber(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Container type
                </span>
                <Input
                  value={containerType}
                  onChange={(e) => setContainerType(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Gross weight
                </span>
                <Input
                  type="number"
                  value={grossWeight}
                  onChange={(e) => setGrossWeight(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Net weight
                </span>
                <Input
                  type="number"
                  value={netWeight}
                  onChange={(e) => setNetWeight(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Packages
                </span>
                <Input
                  type="number"
                  value={packages}
                  onChange={(e) => setPackages(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  ETD
                </span>
                <Input
                  type="date"
                  value={etd}
                  onChange={(e) => setEtd(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  ETA
                </span>
                <Input
                  type="date"
                  value={eta}
                  onChange={(e) => setEta(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Ops notes
                </span>
                <Textarea
                  value={opsNotes}
                  onChange={(e) => setOpsNotes(e.target.value)}
                  className="mt-2 min-h-[70px] rounded-sm"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy}
                variant="outline"
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateShipmentTracking(shipmentId, {
                      vessel: vessel || null,
                      voyage: voyage || null,
                      container_number: containerNumber || null,
                      seal_number: sealNumber || null,
                      container_type: containerType || null,
                      gross_weight: grossWeight !== "" ? Number(grossWeight) : null,
                      net_weight: netWeight !== "" ? Number(netWeight) : null,
                      number_of_packages: packages !== "" ? Number(packages) : null,
                      etd: etd || null,
                      eta: eta || null,
                      ops_notes: opsNotes || null,
                    });
                    toast.success("Shipment updated");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save details & notes
              </Button>

              <Button
                disabled={busy || !eta}
                className="rounded-sm h-10 bg-[hsl(217,90%,25%)] text-white"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateShipmentEta(shipmentId, { eta, etd: etd || null });
                    toast.success("ETA updated");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "ETA update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Update ETA
              </Button>
            </div>

            <div className="border-t border-neutral-100 pt-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                Add event
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  value={eventText}
                  onChange={(e) => setEventText(e.target.value)}
                  placeholder="Event"
                  className="h-10 rounded-sm"
                />
                <Input
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="Location"
                  className="h-10 rounded-sm"
                />
                <Input
                  value={eventRemarks}
                  onChange={(e) => setEventRemarks(e.target.value)}
                  placeholder="Remarks"
                  className="h-10 rounded-sm sm:col-span-2"
                />
              </div>
              <Button
                disabled={busy || !eventText.trim()}
                variant="outline"
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await addShipmentEvent(shipmentId, {
                      event: eventText.trim(),
                      location: eventLocation || null,
                      remarks: eventRemarks || null,
                    });
                    toast.success("Event added");
                    setEventText("");
                    setEventLocation("");
                    setEventRemarks("");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Event failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Add Event
              </Button>
            </div>

            <div className="border-t border-neutral-100 pt-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                Update status
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  value={statusLocation}
                  onChange={(e) => setStatusLocation(e.target.value)}
                  placeholder="Location"
                  className="h-10 rounded-sm"
                />
                <Input
                  value={statusRemarks}
                  onChange={(e) => setStatusRemarks(e.target.value)}
                  placeholder="Remarks"
                  className="h-10 rounded-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.filter((s) => s !== "Cancelled" && s !== "Factory Pickup Pending").map(
                  (status) => (
                    <Button
                      key={status}
                      disabled={busy || trk.status === status}
                      variant="outline"
                      className="rounded-sm h-9 text-xs"
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await updateShipmentStatus(shipmentId, {
                            status,
                            location: statusLocation || null,
                            remarks: statusRemarks || null,
                          });
                          toast.success(`Status: ${status}`);
                          await load();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Status failed");
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      {status}
                    </Button>
                  )
                )}
              </div>
            </div>

            <div className="border-t border-neutral-100 pt-4 space-y-2">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                Correct milestone
              </div>
              <div className="flex flex-wrap gap-2">
                {(trk.milestones || []).map((m) => (
                  <Button
                    key={m.key}
                    disabled={busy}
                    variant="outline"
                    className="rounded-sm h-9 text-xs"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await updateShipmentMilestone(shipmentId, {
                          key: m.key,
                          completed: !(m.completed || m.status === "done"),
                          location: statusLocation || m.location || null,
                          remarks: statusRemarks || m.remarks || null,
                        });
                        toast.success(`Milestone ${m.label} updated`);
                        await load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Milestone failed");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Toggle {m.label}
                  </Button>
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          {trk.freightiq_id && (
            <Link
              to={`/workspace/freightiq/${trk.freightiq_id}`}
              className="text-[hsl(217,90%,25%)] hover:underline"
            >
              ← FreightIQ {trk.freightiq_number}
            </Link>
          )}
          {trk.po_id && (
            <Link
              to={`/workspace/purchase-orders/${trk.po_id}`}
              className="text-[hsl(217,90%,25%)] hover:underline"
            >
              ← Purchase Order {trk.po_number}
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
        {label}
      </div>
      <div className="mt-1 text-neutral-800 break-words">{value ?? "—"}</div>
    </div>
  );
}
